import { Camera, Loader2 } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { useUserProfile } from '@/hooks/useUserProfile';

interface AccountSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  profile: ReturnType<typeof useUserProfile>;
}

const AccountSettingsDialog = ({ open, onOpenChange, profile }: AccountSettingsDialogProps) => {
  const {
    user,
    displayName,
    avatarUrl,
    uploading,
    initials,
    fileInputRef,
    handleAvatarUpload,
  } = profile;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Account Settings</DialogTitle>
        </DialogHeader>
        <div className="space-y-5 pt-2">
          {/* Avatar upload */}
          <div className="flex flex-col items-center gap-3">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="relative cursor-pointer disabled:cursor-wait"
            >
              <Avatar className="h-20 w-20 border-2 border-border">
                <AvatarImage src={avatarUrl || undefined} alt="Avatar" />
                <AvatarFallback className="text-lg">{initials}</AvatarFallback>
              </Avatar>
              <div className="absolute inset-0 flex items-center justify-center rounded-full bg-black/40">
                {uploading ? (
                  <Loader2 className="h-5 w-5 text-white animate-spin" />
                ) : (
                  <Camera className="h-5 w-5 text-white" />
                )}
              </div>
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={handleAvatarUpload}
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="text-xs text-primary hover:underline disabled:opacity-50"
            >
              {uploading ? 'Checking & uploading…' : 'Change profile photo'}
            </button>
            <p className="text-[10px] text-muted-foreground text-center">
              JPG, PNG, or WebP · Max 2MB · Images are screened for appropriateness
            </p>
          </div>

          <Separator />

          <div className="space-y-1.5">
            <Label htmlFor="account-email">Email</Label>
            <Input id="account-email" value={user?.email || ''} disabled className="opacity-60" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="account-displayName">Display Name</Label>
            <Input
              id="account-displayName"
              value={displayName}
              disabled
              className="opacity-60"
            />
            <p className="text-[10px] text-muted-foreground">Display name is set at signup and cannot be changed.</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AccountSettingsDialog;
